"use client";

import { Close, Search as SearchIcon } from "@ellaandil/ui";
import { type FormEvent, type KeyboardEvent, useRef } from "react";
import { IconButton } from "@/components/ui/IconButton";
import { useTranslations } from "@/i18n/client";
import { useRouter } from "@/i18n/routing";
import { useRecentSearches } from "../hooks/useRecentSearches";
import { useSearchModal, useSearchQuery } from "../SearchProvider";

/**
 * Desktop search input rendered in the header. Focuses open the search modal,
 * typing drives the shared search query (consumed by the modal's panel via
 * SearchProvider). The modal hides its own input on desktop so the user only
 * sees one input field.
 */
export const HeaderSearchInput = ({ className }: { className?: string }) => {
	const t = useTranslations();
	const { query, setQuery } = useSearchQuery();
	const { isOpen, open, close } = useSearchModal();
	const router = useRouter();
	const { add: addRecent } = useRecentSearches();
	const inputRef = useRef<HTMLInputElement>(null);

	const submit = () => {
		const trimmed = query.trim();
		if (!trimmed) return;
		addRecent(trimmed);
		close();
		router.push(`/search?q=${encodeURIComponent(trimmed)}`);
	};

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		submit();
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			// Stop Radix's NavigationMenu (the parent) from swallowing Enter for
			// its own keyboard navigation — otherwise the form never submits.
			e.preventDefault();
			e.stopPropagation();
			submit();
			return;
		}
		if (e.key === "Escape") {
			if (query) {
				setQuery("");
			} else {
				close();
				inputRef.current?.blur();
			}
		}
	};

	return (
		<form
			onSubmit={onSubmit}
			className={`${className ?? ""} lg:max-w-[240px] lg:focus-within:max-w-full transition-[max-width] duration-300 ease-out`}
		>
			<label className="flex items-center gap-grid-component-spacing-sm border-b border-solid border-input-default-border hover:border-input-hover-border focus-within:border-input-active-border transition-colors duration-200 py-grid-component-spacing-xs">
				<SearchIcon className="size-5 text-text-dark-text-1" />
				<input
					ref={inputRef}
					type="text"
					name="q"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onFocus={() => {
						if (!isOpen) open();
					}}
					onKeyDown={onKeyDown}
					placeholder={t("Search")}
					autoComplete="off"
					spellCheck={false}
					className="flex-1 bg-transparent outline-none text-sm text-text-dark-text-1 placeholder:text-text-sand-text-3"
				/>
				{(query || isOpen) && (
					<IconButton
						icon={Close}
						size="sm"
						label={query ? t("Clear") : t("Close")}
						onClick={() => {
							if (query) {
								setQuery("");
								inputRef.current?.focus();
							} else {
								close();
								inputRef.current?.blur();
							}
						}}
					/>
				)}
			</label>
		</form>
	);
};
